import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getLaterTime } from 'helpers/helpers'
import {
    BusTicketWithDetails,
    TransportRouteType,
    TransportDepartureType,
} from 'store/api-slice/types'
import { v4 as uuidv4 } from 'uuid'

export type TransportType = 'busTickets'

export type RouteType = {
    ticket: BusTicketWithDetails
    isCompleted: boolean
    isSubmitted: boolean
    duration: string
    unitPrice: number | null
    defaultDates: string[]
    defaultQuantity: number
}

interface TransportState {
    routes: RouteType[]
    edited: {
        index: number
        origin: RouteType
    } | null
}

export const DEFAULT_PRODUCT_TYPE = 'bus_ticket'
export const DEFAULT_ROUTE = 'to_from_park'
export const DEFAULT_DEPARTURE = 'warsaw'
export const DEFAULT_QUANTITY = 1

export const emptyBusTicket: BusTicketWithDetails = {
    cartItemId: '',
    productId: '',
    productType: DEFAULT_PRODUCT_TYPE,
    quantity: DEFAULT_QUANTITY,
    detail: {
        routeType: DEFAULT_ROUTE,
        departurePlace: 'warsaw',
        date: '',
        time: '',
        returnDate: '',
        returnTime: '',
        boxId: '',
        isShuttle: false,
        isRequiresPurchaseTimeSlot: true,
    },
}

const initialState: TransportState = {
    routes: [],
    edited: null,
}

export const getDatesRange = (dates: string[]) =>
    dates && dates.length > 1 ? [dates[0], dates[dates.length - 1]] : dates

export const checkIfCompleted = (
    routeType: TransportRouteType,
    departurePlace: TransportDepartureType,
    toDate: string,
    fromDate: string,
    toTime: string | null,
    fromTime: string | null,
    isRequiresPurchaseTimeSlot: boolean | undefined
) => {
    const isDateToUndefined = routeType !== 'from_park' && toDate === ''
    const isDateFromUndefined = routeType !== 'to_park' && fromDate === ''
    const isTimeToUndefined = isRequiresPurchaseTimeSlot
        ? routeType !== 'from_park' && (toTime === '' || toTime === null)
        : false
    const isTimeFromUndefined = isRequiresPurchaseTimeSlot
        ? routeType !== 'to_park' && (fromTime === '' || fromTime === null)
        : false

    return !(
        isDateToUndefined ||
        isDateFromUndefined ||
        isTimeToUndefined ||
        isTimeFromUndefined
    )
}

const transportSlice = createSlice({
    name: 'transport',
    initialState,
    reducers: {
        addRoute(
            state,
            action: PayloadAction<{
                defaultDates?: string[]
                defaultQuantity?: number
                route?: RouteType
            }>
        ) {
            const { defaultDates, defaultQuantity, route } = action.payload

            const defaultDatesRecalculated = getDatesRange(defaultDates || [])

            if (route) {
                state.routes = [...state.routes, route]
                return
            }

            const newRoute: RouteType = {
                ticket: {
                    ...emptyBusTicket,
                    cartItemId: uuidv4(),
                    quantity: defaultQuantity || DEFAULT_QUANTITY,
                    detail: {
                        ...emptyBusTicket.detail,
                        date:
                            defaultDatesRecalculated &&
                            defaultDatesRecalculated[0]
                                ? defaultDatesRecalculated[0]
                                : '',
                        returnDate:
                            defaultDatesRecalculated &&
                            defaultDatesRecalculated[1]
                                ? defaultDatesRecalculated[1]
                                : defaultDatesRecalculated[0] || '',
                        boxId: uuidv4(),
                    },
                },
                duration: '',
                unitPrice: null,
                isCompleted: false,
                isSubmitted: false,
                defaultDates: defaultDatesRecalculated
                    ? [...defaultDatesRecalculated]
                    : [],
                defaultQuantity: defaultQuantity || DEFAULT_QUANTITY,
            }

            state.routes = [...state.routes, newRoute]
        },

        updateRouteParams(
            state,
            action: PayloadAction<{
                index: number
                routeType?: TransportRouteType
                departurePlace?: TransportDepartureType
                duration?: string
                unitPrice?: number
                isSubmitted?: boolean
                defaultDates?: string[]
            }>
        ) {
            const {
                index,
                routeType,
                departurePlace,
                duration,
                unitPrice,
                isSubmitted,
                defaultDates,
            } = action.payload

            const {
                defaultQuantity,
                defaultDates: localDefaultDates,
                ticket,
                duration: localDuration,
                unitPrice: localUnitPrice,
                isSubmitted: isSubmittedLocal,
            } = state.routes[index]

            const {
                cartItemId,
                quantity: localQuantity,
                detail: {
                    routeType: localRouteType,
                    departurePlace: localDeparturePlace,
                    boxId,
                    isRequiresPurchaseTimeSlot,
                },
            } = ticket

            if (index < 0 || index > state.routes.length - 1) return

            if (
                (localDuration || unitPrice || isSubmitted) &&
                !(routeType || departurePlace)
            ) {
                state.routes[index].duration = duration || localDuration
                state.routes[index].unitPrice = unitPrice || localUnitPrice
                state.routes[index].isSubmitted =
                    isSubmitted || isSubmittedLocal
                return
            }

            const isShuttleRecalculated =
                departurePlace === 'zyrardow' ||
                (!departurePlace && localDeparturePlace === 'zyrardow')

            const defaultDatesRecalculated = getDatesRange(
                defaultDates && defaultDates.length
                    ? defaultDates
                    : localDefaultDates
            )

            const modifiedRoute: RouteType = {
                ticket: {
                    ...emptyBusTicket,
                    cartItemId,
                    quantity: localQuantity || defaultQuantity,
                    detail: {
                        ...emptyBusTicket.detail,
                        routeType: routeType || localRouteType,
                        departurePlace: departurePlace || DEFAULT_DEPARTURE,
                        date:
                            defaultDatesRecalculated &&
                            defaultDatesRecalculated[0]
                                ? defaultDatesRecalculated[0]
                                : '',
                        returnDate:
                            defaultDatesRecalculated &&
                            defaultDatesRecalculated[1]
                                ? defaultDatesRecalculated[1]
                                : defaultDatesRecalculated[0] || '',
                        time: isShuttleRecalculated ? null : '',
                        returnTime: isShuttleRecalculated ? null : '',
                        isShuttle: isShuttleRecalculated,
                        boxId,
                        isRequiresPurchaseTimeSlot,
                    },
                },
                isCompleted: false,
                isSubmitted: isSubmitted || isSubmittedLocal,
                duration: duration || localDuration,
                unitPrice: unitPrice || localUnitPrice,
                defaultDates: defaultDatesRecalculated
                    ? [...defaultDatesRecalculated]
                    : [],
                defaultQuantity,
            }

            modifiedRoute.isCompleted = checkIfCompleted(
                modifiedRoute.ticket.detail.routeType,
                modifiedRoute.ticket.detail.departurePlace,
                modifiedRoute.ticket.detail.date,
                modifiedRoute.ticket.detail.returnDate,
                modifiedRoute.ticket.detail.time,
                modifiedRoute.ticket.detail.returnTime,
                modifiedRoute.ticket.detail.isRequiresPurchaseTimeSlot
            )

            state.routes[index] = { ...modifiedRoute }
        },

        updateRouteTickets(
            state,
            action: PayloadAction<{
                index: number
                ticketDirection?: 'to_park' | 'from_park'
                date?: string
                time?: string
                quantity?: number
                productId?: string
                isRequiresPurchaseTimeSlot?: boolean
            }>
        ) {
            const {
                index,
                ticketDirection,
                date,
                time,
                quantity,
                productId,
                isRequiresPurchaseTimeSlot,
            } = action.payload

            if (index < 0 || index > state.routes.length - 1) return

            const currentRoute = state.routes[index]

            const {
                ticket: { detail, quantity: currentQuantity },
                defaultQuantity,
            } = currentRoute

            const { routeType, departurePlace } = detail

            const newQuantity = quantity
                ? quantity
                : currentQuantity || defaultQuantity

            if (ticketDirection === 'to_park') {
                if (date) {
                    detail.date = date
                    detail.returnDate = detail.returnDate || date
                    !time && (detail.time = '')
                }
                time && (detail.time = time)

                // clear departure time if it preceeds arrival
                if (
                    routeType === 'to_from_park' &&
                    detail.date === detail.returnDate &&
                    time &&
                    detail.time &&
                    detail.returnTime &&
                    getLaterTime(detail.time || '', detail.returnTime) === 0
                ) {
                    detail.returnTime = ''
                }

                // clear departure time and align dates if it's date preceeds arrival
                if (
                    routeType === 'to_from_park' &&
                    date &&
                    detail.returnDate &&
                    detail.date > detail.returnDate
                ) {
                    detail.returnDate = date
                    detail.returnTime = ''
                }
            } else if (ticketDirection === 'from_park') {
                if (date) {
                    detail.returnDate = date
                    detail.date = detail.date || date
                    !time && (detail.returnTime = '')
                }
                time && (detail.returnTime = time)
            }

            if (!productId || (productId && quantity)) {
                currentRoute.ticket.quantity = newQuantity
            }

            if (productId) {
                currentRoute.ticket.productId = productId
            }

            // keep constant fields values for one direction tickets
            if (routeType === 'to_park') {
                detail.returnDate = detail.date
                detail.returnTime = detail.time
            } else if (routeType === 'from_park') {
                detail.date = detail.returnDate
                detail.time = detail.returnTime
            }

            detail.isRequiresPurchaseTimeSlot =
                isRequiresPurchaseTimeSlot !== undefined
                    ? isRequiresPurchaseTimeSlot
                    : true

            currentRoute.isCompleted = checkIfCompleted(
                routeType,
                departurePlace,
                detail.date,
                detail.returnDate,
                detail.time,
                detail.returnTime,
                detail.isRequiresPurchaseTimeSlot
            )
        },

        setEditRoute(
            state,
            action: PayloadAction<{
                index: number
                editState: 'editing' | 'declined' | 'completed'
            }>
        ) {
            const { index, editState } = action.payload
            const { routes, edited } = state

            if (routes.length === 0 || index > routes.length - 1 || index < 0)
                return

            // restore state for currently edited route
            if (edited !== null) {
                if (editState !== 'completed')
                    state.routes[edited.index] = { ...edited.origin }

                state.edited = null
            }

            // set desired route as edited
            if (editState === 'editing') {
                state.edited = {
                    index,
                    origin: {
                        ...routes[index],
                        ticket: { ...routes[index].ticket },
                    },
                }
                state.routes[index].ticket.cartItemId = uuidv4()
            }
        },

        cleanTransport(state) {
            const { edited, routes } = state

            if (edited !== null)
                state.routes[edited.index] = { ...edited.origin }

            state.routes = routes.filter((route) => route.isSubmitted)
            state.edited = null
        },

        removeRoute(
            state,
            action: PayloadAction<{
                index: number
            }>
        ) {
            const { index } = action.payload

            if (
                state.routes.length === 0 ||
                index > state.routes.length - 1 ||
                index < 0
            )
                return

            state.routes.splice(index, 1)
        },

        removeTransport(state) {
            state.routes = []
            state.edited = null
        },

        synchronizeTicketsWithCart(state, action: PayloadAction<RouteType[]>) {
            state.routes = action.payload
        },
    },
})

export const {
    addRoute,
    updateRouteParams,
    updateRouteTickets,
    setEditRoute,
    cleanTransport,
    removeRoute,
    removeTransport,
    synchronizeTicketsWithCart,
} = transportSlice.actions
export default transportSlice.reducer
